import type { StyleProps } from "@chakra-ui/react";
import { Flex, Text } from "@chakra-ui/react";
import { Progress } from "../progress/Progress";
import type { ThemeColors } from "../../theme/tokens/colors";

export const STRENGTH = {
  WEAK: "weak",
  STRONG: "strong",
} as const;

export type Strength = (typeof STRENGTH)[keyof typeof STRENGTH];

export interface PasswordStrengthProps extends StyleProps {
  strength: Strength | number;
  threshold?: number;
  validationRuleText?: string;
}

const passwordStrengthConfig = {
  [STRENGTH.WEAK]: {
    text: "Weak",
    color: "error.fg" as ThemeColors,
    filledWidth: 40,
  },
  [STRENGTH.STRONG]: {
    text: "Strong!",
    color: "success.fg" as ThemeColors,
    filledWidth: 82.5,
  },
};

export const PasswordStrength = ({
  strength,
  threshold = 80,
  validationRuleText,
  ...customStyles
}: PasswordStrengthProps) => {
  const isNumberStrength = typeof strength === "number";

  const { text, color, filledWidth } = isNumberStrength
    ? strength < threshold
      ? passwordStrengthConfig[STRENGTH.WEAK]
      : passwordStrengthConfig[STRENGTH.STRONG]
    : passwordStrengthConfig?.[strength];
  return (
    <Flex alignItems="center" data-cy="password-strength" {...customStyles}>
      <Progress
        value={typeof strength === "number" ? strength : filledWidth}
        color={color}
        width="125px"
      />
      <Text color={color} textStyle="caption" ml="2">
        <span>{text}</span>
        <span>
          {validationRuleText &&
            isNumberStrength &&
            strength < threshold &&
            ` (${validationRuleText})`}
        </span>
      </Text>
    </Flex>
  );
};
